/**
 * Accessibility utility functions for focus management and screen reader support
 */

/**
 * Traps focus within a given element (useful for modals/dialogs)
 * @param element - The element to trap focus within
 * @returns Cleanup function to remove focus trap
 */
export function trapFocus(element: HTMLElement): () => void {
  const focusableElements = element.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  function handleTabKey(e: KeyboardEvent) {
    if (e.key !== "Tab") return;

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  }

  element.addEventListener("keydown", handleTabKey);

  // Focus first element
  firstElement?.focus();

  // Return cleanup function
  return () => {
    element.removeEventListener("keydown", handleTabKey);
  };
}

/**
 * Announces a message to screen readers using a live region
 * @param message - The message to announce
 * @param priority - The priority level (polite or assertive)
 */
export function announceToScreenReader(
  message: string,
  priority: "polite" | "assertive" = "polite"
): void {
  // Create or get live region
  let liveRegion = document.getElementById("a11y-live-region");

  if (!liveRegion) {
    liveRegion = document.createElement("div");
    liveRegion.id = "a11y-live-region";
    liveRegion.setAttribute("role", "status");
    liveRegion.setAttribute("aria-live", priority);
    liveRegion.setAttribute("aria-atomic", "true");
    liveRegion.className = "sr-only";
    document.body.appendChild(liveRegion);
  }

  // Update message
  liveRegion.textContent = message;

  // Clear message after announcement (for repeated announcements)
  setTimeout(() => {
    if (liveRegion) {
      liveRegion.textContent = "";
    }
  }, 1000);
}

/**
 * Moves focus to the main content area (prefers h1 for better screen reader experience)
 * Useful after route changes
 */
export function focusMainContent(): void {
  // Prefer h1 for better screen reader experience, fallback to main
  const h1 = document.querySelector("h1");
  const main = document.querySelector("main");
  const target = h1 || main;

  if (target instanceof HTMLElement) {
    // Make sure element is focusable
    if (!target.hasAttribute("tabindex")) {
      target.setAttribute("tabindex", "-1");
    }
    target.focus();
    // Respect reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  }
}

/**
 * Restores focus to a previously focused element
 * Useful when closing modals/dialogs
 */
export function restoreFocus(previousElement: HTMLElement | null): void {
  if (previousElement && document.contains(previousElement)) {
    previousElement.focus();
  }
}

/**
 * Gets the currently focused element
 * @returns The currently focused element or null
 */
export function getFocusedElement(): HTMLElement | null {
  return document.activeElement instanceof HTMLElement ? document.activeElement : null;
}
